import { DropboxService, DropboxWrapperApiService } from '@aipb/dropbox/data-access';
import { PersonalContentPermissionService } from '@aipb/personal-content/shared/auth';
import { FavoriteNibbleService, NibbleApiService } from '@aipb/personal-content/data-access';
import { CreateEditNibbleDialogService } from '@aipb/personal-content/dialog';
import { NibbleMenuComponent } from '@aipb/personal-content/feature-nibble-menu';
import { Collection, NibbleCreateModel, NibbleCut, NibbleMenuContext } from '@aipb/personal-content/shared/model';
import { NibbleTypeIconComponent } from '@aipb/personal-content/ui';
import { SafeHtmlPipe, ShortenPipe, StripHtmlPipe } from '@aipb/ui/pipes';
import { CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { AfterViewChecked, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { forkJoin, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { ImageComponent } from '@aipb/ui/image';

@UntilDestroy()
@Component({
  selector: 'pc-shared-nibble-list',
  templateUrl: './nibble-list.component.html',
  styleUrls: ['./nibble-list.component.scss'],
  imports: [
    CommonModule,
    MatIconModule,
    TranslocoModule,
    ImageComponent,
    NibbleTypeIconComponent,
    NibbleMenuComponent,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    MatCheckboxModule,
    DragDropModule,
    StripHtmlPipe,
    ShortenPipe,
    SafeHtmlPipe
  ]
})
export class NibbleListComponent implements OnChanges, AfterViewChecked {
  // @ts-expect-error foo
  @Input() collection: Collection;
  @Input() nibbles: NibbleCut[] = [];
  @Input() bulkEdit = false;
  // @ts-expect-error foo
  @Input() highlightId: number;
  // @ts-expect-error foo
  @Input() context: NibbleMenuContext;
  @Input() selected: {
    [key: string]: boolean;
  } = {};

  @Output() createEnded: EventEmitter<NibbleCreateModel> = new EventEmitter();
  @Output() editEnded: EventEmitter<NibbleCut> = new EventEmitter();
  @Output() deleteEnded: EventEmitter<void> = new EventEmitter();
  @Output() action: EventEmitter<[NibbleCut, number, string]> = new EventEmitter();
  @Output() sortEnded: EventEmitter<NibbleCut> = new EventEmitter();
  @Output() selectionChange = new EventEmitter<[NibbleCut, boolean]>();
  @Output() copyMoveEnded = new EventEmitter<NibbleCut>();

  filter: UntypedFormControl = new UntypedFormControl();

  data: NibbleCut[] = [];
  // @ts-expect-error foo
  filteredData$: Observable<NibbleCut[]>;
  dropboxImages: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [id: string]: any;
  } = {};

  constructor(
    private favoriteNibbleService: FavoriteNibbleService,
    private dropbox: DropboxService,
    private cdr: ChangeDetectorRef,
    private dropboxWrapperApi: DropboxWrapperApiService,
    private transloco: TranslocoService,
    private sanitizer: DomSanitizer,
    private personalContentPermission: PersonalContentPermissionService,
    private createEditNibbleDialogService: CreateEditNibbleDialogService,
    private nibbleApi: NibbleApiService
  ) {}

  get hideCollections(): boolean {
    return !this.personalContentPermission.canUseCollections;
  }

  getSelected(nibble: NibbleCut) {
    if (nibble.nibble.positionId in this.selected) {
      return this.selected[nibble.nibble.positionId];
    }

    return false;
  }

  ngOnChanges(): void {
    if (this.nibbles) {
      this.data = JSON.parse(JSON.stringify(this.nibbles));

      this.filteredData$ = this.filter.valueChanges.pipe(
        startWith(''),
        map((value) => (typeof value === 'string' ? value : '')),
        map((label) => (label ? this._filter(label) : this.data.slice()))
      );

      const dropboxNibbles = this.nibbles.filter((f) => f.nibble.type === 'RECDBOX');

      if (dropboxNibbles.length === 0) {
        return;
      }

      const subs = {};

      dropboxNibbles.forEach((d) => {
        // @ts-expect-error foo
        subs[d.cut.serviceValue] = this.dropbox.imageDownload(d.cut.serviceValue);
      });

      forkJoin(subs).subscribe((data) => {
        for (const k of Object.keys(data)) {
          const imageUrl = URL.createObjectURL(data[k]);
          this.dropboxImages[k] = this.sanitizer.bypassSecurityTrustUrl(imageUrl);
        }

        this.cdr.detectChanges();
      });
    }
  }

  ngAfterViewChecked(): void {
    if (!this.highlightId) return;

    const el = document.getElementById('nibble-list-' + this.highlightId);

    if (!el) return;

    el.scrollIntoView({
      behavior: 'smooth'
    });
  }

  onNibbleOpen(nibbleCut: NibbleCut, position: number): void {
    this.action.emit([nibbleCut, position, this.collection.id]);
  }

  onEditNibble(nibbleCut: NibbleCut): void {
    this.createEditNibbleDialogService
      .open$({
        editNibble: nibbleCut
      })
      .subscribe(() => {
        this.editEnded.emit(nibbleCut);
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  drop(event: CdkDragDrop<NibbleCut[] | null, any>) {
    // @ts-expect-error foo
    const item: NibbleCut = <any>event.container.data[event.previousIndex];

    if (event.previousContainer === event.container) {
      this.favoriteNibbleService
        .changePosition(item['nibble'].id, this.collection.id, event.currentIndex, event.previousIndex)
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.sortEnded.emit(item);
        });
      // @ts-expect-error foo
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      // @ts-expect-error foo
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }

    this.data = [...this.data];
  }

  hasThumb(nibbleCut: NibbleCut): boolean {
    const type = nibbleCut.nibble.type;
    return !!nibbleCut.nibble.userThumb || !!nibbleCut.cut.serviceThumb || type === 'IMG' || type === 'IMG3F';
  }

  getThumb(nibbleCut: NibbleCut): string {
    const type = nibbleCut.nibble.type;

    if (type === 'RECDBOX') {
      return this.dropboxImages[nibbleCut.cut.serviceValue];
    }

    if (nibbleCut.nibble.userThumb) {
      return nibbleCut.nibble.userThumb;
    }

    if (nibbleCut.cut.serviceThumb) {
      return nibbleCut.cut.serviceThumb;
    }

    if (type === 'IMG' || type === 'IMG3F') {
      return nibbleCut.nibble.userValue || nibbleCut.cut.serviceValue;
    }

    // @ts-expect-error foo
    return null;
  }

  // @ts-expect-error foo
  nibbleById(index, item) {
    return item.nibble.id;
  }

  onDeleteAll() {
    this.nibbleApi
      .removeFromCollectionById({
        collId: this.collection.id,
        id: this.nibbles.map((n) => n.nibble.id),
        positionId: this.nibbles.map((n) => n.nibble.positionId)
      })
      .subscribe(() => {
        this.deleteEnded.emit();
      });
  }

  private _filter(label: string): NibbleCut[] {
    const filterValue = label.toLowerCase();

    return this.data.filter((nibbleCut) => {
      if (nibbleCut.nibble.userLabel) {
        return nibbleCut.nibble.userLabel.toLowerCase().includes(filterValue);
      }

      if (nibbleCut.cut.serviceLabel) {
        return nibbleCut.cut.serviceLabel.toLowerCase().includes(filterValue);
      }

      return false;
    });
  }
}
